import React, { useState } from 'react';
import { addEventPayment } from '../../utils/eventApi';
import { PAYMENT_TYPES, defaultPaymentType, formatMoney, todayIso } from '../../utils/eventUtils';

// Records money handed to the vendor: toward one line item, or toward the whole event.
function RecordPaymentModal({ event, users, currentUser, initialItemId, onClose, onSuccess }) {
  const members = users.filter((u) => event.memberIds.includes(u.id));

  // What is still due on the chosen target ('' means the whole event).
  const targetStatus = (itemId) => {
    const item = itemId ? event.summary.items.find((i) => i.itemId === itemId) : null;
    const source = item || event.summary;
    return {
      name: item ? item.name : event.name,
      paid: Number(source.paid),
      outstanding: Number(source.outstanding),
    };
  };

  const initialTarget = targetStatus(initialItemId || '');
  const [itemId, setItemId] = useState(initialItemId || '');
  const [label, setLabel] = useState(initialTarget.name);
  const [labelEdited, setLabelEdited] = useState(false);
  const [paidByUserId, setPaidByUserId] = useState(
    members.some((u) => u.id === currentUser.id) ? currentUser.id : members[0]?.id || ''
  );
  const [amount, setAmount] = useState(initialTarget.outstanding > 0 ? initialTarget.outstanding.toFixed(2) : '');
  const [type, setType] = useState(
    defaultPaymentType(initialTarget.outstanding, initialTarget.paid, initialTarget.outstanding)
  );
  const [typeEdited, setTypeEdited] = useState(false);
  const [paidOn, setPaidOn] = useState(todayIso());
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const target = targetStatus(itemId);
  // Item payments wait for that item's split; whole-event payments wait for every item's.
  const awaitsSplit = event.summary.items.some((i) => (!itemId || i.itemId === itemId) && !i.splitComplete);
  const amountValue = parseFloat(amount);
  const isOverpayment = !isNaN(amountValue) && amountValue > target.outstanding + 0.001;

  const handleTargetChange = (newItemId) => {
    const next = targetStatus(newItemId);
    setItemId(newItemId);
    setAmount(next.outstanding > 0 ? next.outstanding.toFixed(2) : '');
    if (!labelEdited) setLabel(next.name);
    if (!typeEdited) setType(defaultPaymentType(next.outstanding, next.paid, next.outstanding));
  };

  const handleAmountChange = (value) => {
    setAmount(value);
    if (!typeEdited) setType(defaultPaymentType(value, target.paid, target.outstanding));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!label.trim()) {
      setError('Please give this payment a name.');
      return;
    }
    if (isNaN(amountValue) || amountValue <= 0) {
      setError('Please provide a valid payment amount.');
      return;
    }
    if (isOverpayment) {
      setError(`Only ${formatMoney(target.outstanding)} is still due on ${target.name}.`);
      return;
    }

    setLoading(true);
    try {
      await addEventPayment(event.id, {
        itemId: itemId || null,
        label: label.trim(),
        paidByUserId,
        amount: amountValue,
        type,
        paidOn,
      });
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Error recording payment.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content animate-slide-up">
        <div className="modal-header">
          <h3>💸 Record Payment</h3>
          <button className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-group">
            <label htmlFor="payment-target">Payment For</label>
            <select id="payment-target" value={itemId} onChange={(e) => handleTargetChange(e.target.value)}>
              <option value="">Whole event ({formatMoney(event.summary.outstanding)} due)</option>
              {event.summary.items.map((item) => (
                <option key={item.itemId} value={item.itemId}>
                  {item.name} ({formatMoney(item.outstanding)} due)
                </option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="payment-label">Payment Name</label>
            <input
              id="payment-label"
              type="text"
              maxLength={80}
              placeholder="e.g. Hotel advance, Checkout"
              value={label}
              onChange={(e) => {
                setLabel(e.target.value);
                setLabelEdited(true);
              }}
              required
            />
          </div>

          <div className="form-row-2">
            <div className="form-group">
              <label htmlFor="payment-amount">Amount (₹)</label>
              <input
                id="payment-amount"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="payment-payer">Paid By</label>
              <select id="payment-payer" value={paidByUserId} onChange={(e) => setPaidByUserId(e.target.value)}>
                {members.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.id === currentUser.id ? 'You' : u.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={`remaining-amount-banner ${isOverpayment ? 'mismatch' : 'zero'}`}>
            {isOverpayment ? (
              <span>
                ⚠️ Only <strong>{formatMoney(target.outstanding)}</strong> is still due on {target.name}
              </span>
            ) : (
              <span>
                {formatMoney(target.outstanding)} due on {target.name}
              </span>
            )}
          </div>

          {awaitsSplit && (
            <div className="remaining-amount-banner pending">
              <span>
                The split is not finished yet. This payment will be recorded now and counted in balances once
                the split is complete.
              </span>
            </div>
          )}

          <div className="form-row-2">
            <div className="form-group">
              <label htmlFor="payment-type">Payment Type</label>
              <select
                id="payment-type"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setTypeEdited(true);
                }}
              >
                {PAYMENT_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="payment-date">Paid On</label>
              <input id="payment-date" type="date" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} required />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={loading || isOverpayment}>
              {loading ? 'Recording...' : 'Record Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RecordPaymentModal;
